import {
  Box,
  Button,
  ContentLayout,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import { LineItemTypes } from '@parallel-fluidics/shared/constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Form,
  Formik,
} from 'formik';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as Yup from 'yup';

import CustomerInfo from './components/customerInfo';
import EstimatePreviewTable from './components/estimatePreviewTable';
import LineItemInfo, { defaultProductLineItem } from './components/lineItemInfo';
import ShippingInfo from './components/shippingInfo';
import { getPreparedEstimateValues } from './estimatePreview';

const addressSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  phone: Yup.string().required('Phone number is required'),
  street1: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string(),
  zip: Yup.string().required('Zip Code is required'),
  country: Yup.string().required('Country is required'),
});

const productLineItemSchema = Yup.object().shape({
  type: Yup.string().oneOf([LineItemTypes.PRODUCT]).required('Type is required'),
  productDocId: Yup.string().required('SKU is required'),
  productVersionId: Yup.string().required('Version is required'),
  sku: Yup.string().required('SKU is required'),
  name: Yup.string().required('Name is required'),
  projectId: Yup.string().required('Product version project is required'),
  projectDocId: Yup.string().nullable(),
  pPartId: Yup.string().required('Product version pPart is required'),
  pPartDocId: Yup.string().nullable(),
  description: Yup.string(),
  quantity: Yup.number().positive().required('Quantity is required'),
  unitPrice: Yup.number().min(0).required('Unit price is required'),
  leadtimeDays: Yup.number().positive().integer().required('Lead time is required'),
});

const onDemandLineItemSchema = Yup.object().shape({
  type: Yup.string().oneOf([LineItemTypes.ON_DEMAND]).required('Type is required'),
  projectId: Yup.string().required('Project is required'),
  projectDocId: Yup.string().required('Project is required'),
  pPartId: Yup.string().required('Part is required'),
  pPartDocId: Yup.string(),
  bonding: Yup.string(),
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  quantity: Yup.number().positive().required('Quantity is required'),
  unitPrice: Yup.number().min(0).required('Unit price is required'),
  leadtimeDays: Yup.number().positive().integer().required('Lead time is required'),
});

const lineItemSchema = Yup.lazy((value) => (
  value?.type === LineItemTypes.ON_DEMAND
    ? onDemandLineItemSchema
    : productLineItemSchema
));

const createEstimateSchema = Yup.object().shape({
  organization: Yup.string().required('Organization is required'),
  uid: Yup.string().required('Customer is required'),
  addressData: addressSchema.required('Address is required'),
  includeShipping: Yup.boolean(),
  shipping: Yup.object().when('includeShipping', {
    is: true,
    then: Yup.object().shape({
      displayName: Yup.string().required('Shipping name is required'),
      total: Yup.number().min(0).required('Shipping fee is required'),
      unitPrice: Yup.number().min(0).nullable(),
      packageCount: Yup.number().positive().integer().required('Package count is required'),
    }).required('Shipping is required'),
    otherwise: Yup.object().nullable(),
  }),
  lineItems: Yup.array().of(lineItemSchema).min(1, 'At least one line item is required'),
});

const initialValues = {
  organization: '',
  uid: '',
  addressData: {
    name: '',
    company: '',
    phone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  },
  includeShipping: false,
  shipping: {
    carrier: '',
    service: '',
    rateName: '',
    displayName: 'Shipping',
    unitPrice: null,
    packageCount: 1,
    total: null,
  },
  lineItems: [{ ...defaultProductLineItem }],
};

export default function CreateEstimatePage() {
  const navigate = useNavigate();

  const handleSubmit = useCallback(async (values) => {
    try {
      const createManualQBOEstimate = httpsCallable(getFunctions(), 'createManualQBOEstimateV2');
      const { data } = await createManualQBOEstimate(getPreparedEstimateValues(values));
      toast.success(`QBO estimate ${data.docNumber || data.estimateId} created successfully.`);
      if (data.pdfUrl) {
        window.location.href = data.pdfUrl;
      } else {
        navigate('/qbo/estimates');
      }
    } catch (error) {
      toast.error(`Failed to create QBO estimate. ${error.message}`);
    }
  }, [navigate]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createEstimateSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        try {
          setStatus(null);
          await handleSubmit(values);
        } catch (error) {
          setStatus(error.message);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, status }) => (
        <Form>
          <ContentLayout header={<Header variant="h1">Create QBO Estimate Manually</Header>}>
            <SpaceBetween size="m">
              <CustomerInfo />

              <ShippingInfo />

              <LineItemInfo />

              <EstimatePreviewTable />

              {status ? (
                <Box color="text-status-error">
                  {status}
                </Box>
              ) : null}

              <Box textAlign="right">
                <Button variant="primary" type="submit" loading={isSubmitting}>
                  Create Estimate
                </Button>
              </Box>
            </SpaceBetween>
          </ContentLayout>
        </Form>
      )}
    </Formik>
  );
}
